// Modes de jeu EXPÉRIMENTAUX (axe « archétypes »). Système indépendant et
// réversible : le mode CLASSIC reproduit le jeu actuel à l'identique. Les autres
// ne sont accessibles que via le menu SELECT, lui-même gardé par EXPERIMENTS.
//   EXPERIMENTS = false  → seul Classique existe = jeu d'aujourd'hui.
//   (et tant que la branche n'est pas mergée, la prod ne bouge pas)

export const EXPERIMENTS = true;

export interface Mode {
  key: string;
  label: string;
  /** Durée de la partie (s) ; null = pas de chrono (Survie / Série). */
  duration: number | null;
  /** Fin de partie à N ratés ; null = pas de système de vies. */
  lives: number | null;
  /** Victoire quand la série atteint N ; null = désactivé. */
  streakGoal: number | null;
  /** Seules les réussites PARFAIT rapportent des points. */
  perfectOnly: boolean;
  /** Multiplicateur de vitesse de base (Sprint). */
  speedMult: number;
  /** Base de l'accélération : "time" (avec le temps) ou "streak" (avec la série,
   *  se remet à zéro à chaque raté). Défaut : "time". */
  accelBy?: "time" | "streak";
  /** Mouvement de la main : "bounce" (va-et-vient, défaut) ou "drift" (défilé
   *  continu gauche→droite qui reboucle). */
  movement?: "bounce" | "drift";
  /** Amplitude (px) de l'oscillation horizontale lente du nez. 0/absent = fixe. */
  noseSway?: number;
  /** Amplitude (degrés) du tangage du nez (rotation autour du haut-milieu). */
  noseTilt?: number;
  /** Amplitude du rétrécissement pulsé du nez (0.12 = jusqu'à -12 %). */
  noseZoom?: number;
}

// Classique = exactement le jeu d'aujourd'hui (30 s, score précision × combo).
export const CLASSIC: Mode = {
  key: "classic",
  label: "Classique",
  duration: 30,
  lives: null,
  streakGoal: null,
  perfectOnly: false,
  speedMult: 1,
};

// Sprint : court et rapide, max de points en 15 s.
const SPRINT: Mode = {
  key: "sprint",
  label: "Sprint 15s",
  duration: 15,
  lives: null,
  streakGoal: null,
  perfectOnly: false,
  speedMult: 1.4,
};

// Survie : pas de chrono, 3 ratés et c'est fini (la vitesse monte avec le temps).
const SURVIE: Mode = {
  key: "survie",
  label: "Survie 3 vies",
  duration: null,
  lives: 3,
  streakGoal: null,
  perfectOnly: false,
  speedMult: 1,
};

// Série : enchaîner 12 réussites d'affilée. La vitesse monte AVEC la série (et
// retombe à chaque raté) ; 6 ratés et la partie s'arrête.
const SERIE: Mode = {
  key: "serie",
  label: "Série de 12",
  duration: null,
  lives: 6,
  streakGoal: 12,
  perfectOnly: false,
  speedMult: 1,
  accelBy: "streak",
};

// Sniper : 30 s, mais seules les réussites PARFAIT comptent.
const SNIPER: Mode = {
  key: "sniper",
  label: "Sniper",
  duration: 30,
  lives: null,
  streakGoal: null,
  perfectOnly: true,
  speedMult: 1,
};

// Défilé : la main glisse lentement et en continu de gauche à droite (boucle),
// sans rebond. Rythme posé, on guette le bon passage.
const DEFILE: Mode = {
  key: "defile",
  label: "Défilé",
  duration: 30,
  lives: null,
  streakGoal: null,
  perfectOnly: false,
  speedMult: 0.5,
  movement: "drift",
};

// Nez mobile : le nez oscille lentement de gauche à droite → narines mouvantes.
const NEZ_MOBILE: Mode = {
  key: "nezmobile",
  label: "Nez mobile",
  duration: 30,
  lives: null,
  streakGoal: null,
  perfectOnly: false,
  speedMult: 0.9,
  noseSway: 26,
};

// Nez pivotant : le nez tangue de ±10° autour du haut-milieu, avec une pulsation
// de rétrécissement → narines qui se balancent et changent un peu de taille.
const NEZ_PIVOT: Mode = {
  key: "nezpivot",
  label: "Nez pivotant",
  duration: 30,
  lives: null,
  streakGoal: null,
  perfectOnly: false,
  speedMult: 0.9,
  noseTilt: 10,
  noseZoom: 0.12,
};

export const MODES: Mode[] = [
  CLASSIC,
  SPRINT,
  SURVIE,
  SERIE,
  SNIPER,
  DEFILE,
  NEZ_MOBILE,
  NEZ_PIVOT,
];

const MODE_KEY = "the-big-dig.mode";

export function loadMode(): Mode {
  try {
    const k = localStorage.getItem(MODE_KEY);
    const m = MODES.find((x) => x.key === k);
    if (m) return m;
  } catch {
    /* stockage indisponible */
  }
  return CLASSIC;
}

export function saveMode(mode: Mode): void {
  try {
    localStorage.setItem(MODE_KEY, mode.key);
  } catch {
    /* stockage indisponible */
  }
}
